import { useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';
import { api } from '../api.ts';
import { cart, orderLocation, useOrderLocation } from '../lib/cart.ts';

interface PublicLocation {
  id: string;
  slug: string;
  name: string;
  orderingEnabled: boolean;
  address: { line1?: string; city?: string } | null;
}

const CSS = `
.lp { display: grid; gap: 6px; margin: 0 0 16px; }
.lp-label { font-size: 13px; font-weight: 600; letter-spacing: .04em; text-transform: uppercase; color: var(--ink-soft); padding: 0 4px; }
.lp-list { display: grid; background: var(--paper); border-radius: 16px; overflow: hidden;
  box-shadow: 0 1px 2px rgba(20,20,20,.04), 0 0 0 1px rgba(20,20,20,.05); }
.lp-row { display: flex; align-items: center; gap: 12px; min-height: 56px; padding: 8px 16px; border: 0; background: none;
  font: inherit; color: var(--ink); text-align: left; cursor: pointer; width: 100%; }
.lp-row + .lp-row { box-shadow: inset 0 1px 0 var(--line); }
.lp-row span { flex: 1; min-width: 0; display: grid; }
.lp-row small { color: var(--ink-soft); font-size: 14px; }
.lp-dot { flex: none; width: 22px; height: 22px; border-radius: 50%; border: 2px solid #c9c3ba; }
.lp-row[aria-checked='true'] .lp-dot { border: 7px solid var(--crave-red); }
`;

/**
 * "Pick up at": shown only when more than one location takes orders. Choosing another location
 * empties the cart, since prices and stock belong to a location.
 */
export function LocationPicker() {
  const current = useOrderLocation();
  const q = useQuery({
    queryKey: ['public-locations'],
    queryFn: async () =>
      (await api.get<{ items: PublicLocation[] }>('/public/locations')).data.items,
    staleTime: 5 * 60_000,
  });
  const all = q.data ?? [];
  const open = all.filter((l) => l.orderingEnabled);
  const choices = open.length > 0 ? open : all;

  // A remembered location that closed or was removed falls back to one that takes orders.
  useEffect(() => {
    if (choices.length > 0 && !choices.some((l) => l.slug === current))
      orderLocation.set(choices[0]!.slug, { keepCart: cart.units() === 0 });
  }, [choices, current]);

  if (choices.length < 2) return null;
  return (
    <div className="lp" role="radiogroup" aria-label="Pick up at">
      <style href="crave-location-picker" precedence="default">
        {CSS}
      </style>
      <span className="lp-label">Pick up at</span>
      <div className="lp-list">
        {choices.map((l) => (
          <button
            key={l.id}
            type="button"
            role="radio"
            aria-checked={l.slug === current}
            className="lp-row"
            onClick={() => {
              if (l.slug === current) return;
              if (
                cart.units() > 0 &&
                !window.confirm(
                  `Switch to ${l.name}? Your cart will be emptied, since each location has its own menu.`,
                )
              )
                return;
              orderLocation.set(l.slug);
            }}
          >
            <i className="lp-dot" aria-hidden="true" />
            <span>
              <b>{l.name}</b>
              {l.address?.line1 && (
                <small>
                  {l.address.line1}
                  {l.address.city ? `, ${l.address.city}` : ''}
                </small>
              )}
            </span>
          </button>
        ))}
      </div>
    </div>
  );
}
